import Permissions, { usePermissions } from '@app/src/auth/permissions'
import ScreenedAndReportedResponseAssessmentCell from '@app/src/components/Table/Cells/AssessmentResultCell'
import CompanyCell from '@app/src/components/Table/Cells/CompanyCell'
import DateCell from '@app/src/components/Table/Cells/DateCell'
import ExternalVerificationStatusCell from '@app/src/components/Table/Cells/ExternalVerificationStatusCell'
import LinkCell from '@app/src/components/Table/Cells/LinkCell'
import PeriodNameCell from '@app/src/components/Table/Cells/PeriodNameCell'
import RequestCell from '@app/src/components/Table/Cells/RequestCell'
import StatusCell from '@app/src/components/Table/Cells/StatusCell'
import ValueCell from '@app/src/components/Table/Cells/ValueCell'
import { useAccount } from '@app/src/context/AccountContext'
import { useCompleteAutomationSummary } from '@app/src/hooks/useAutomationSummary'
import type { Response } from '@app/src/types/resourceExplorer'
import { ExternalVerificationStatus } from '@app/src/types/resourceExplorer'
import { getAutomationTooltip } from '@app/src/utils/automationTooltipHelper'
import paths, { productReporting, reporting } from '@app/src/wf-constants/paths'
import { TableRow } from '@mui/material'
import markdownToTxt from 'markdown-to-txt'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router-dom'

interface AccessorResponseRowProps {
  row: Response
  showTargetProductColumn?: boolean
}

export const AccessorResponseRow = ({
  row: response,
  showTargetProductColumn,
}: AccessorResponseRowProps): JSX.Element => {
  const { hasPermission } = usePermissions()
  const history = useHistory()
  const verificationPermitted = hasPermission(Permissions.WRITE_VERIFICATION)
  const { account } = useAccount()
  const { formatMessage } = useIntl()

  const inquiry = response?.request?.inquiries?.find(
    inquiry => inquiry.creatorOrganizationId === account?.organization?.id,
  )

  const provider = response?.request?.subscriptions.find(
    subscription => subscription.target.id === response?.request?.targetAliasObject.id,
  )?.target

  const assessment = provider?.assessments?.find(
    assessment =>
      assessment.periodName === response?.request?.periodName &&
      assessment.assessmentTemplate.questionnaireTemplateId === response?.request?.questionnaireTemplateId,
  )

  // Compute automation tooltip
  // NOTE: This hook is called per row, BUT React Query caches API calls by automation ID
  // So if 100 rows have the same automation, the API is called only once
  const automation = inquiry?.requestAutomation
  const automationSummary = useCompleteAutomationSummary(automation)
  const automationTooltip = automation ? getAutomationTooltip(automation, automationSummary, formatMessage) : undefined

  return (
    <TableRow>
      <ExternalVerificationStatusCell
        responseStatus={response?.verifications?.[0]?.externalVerificationStatus ?? ExternalVerificationStatus.NotSet}
        draftStatus={response?.draftStatus}
        inquiryStatus={inquiry?.status}
      />
      <RequestCell
        title={response.request.title}
        imageUrl={response.request?.template?.image?.url}
        isScraped={response.isScraped}
        onClick={() => {
          const path = response.request.targetProductId ? productReporting : reporting
          history.push({ pathname: path, search: `requestId=${response.request.id}&responseId=${response.id}` })
        }}
      />
      {showTargetProductColumn &&
        (response.request.targetProduct ? (
          <LinkCell
            label={response.request.targetProduct?.name}
            to={generatePath(paths.product, { id: response.request.targetProduct?.id })}
          />
        ) : (
          <ValueCell />
        ))}
      <PeriodNameCell periodName={response.request?.periodName} />
      <CompanyCell
        company={{ ...response?.request?.targetAliasObject, organization: response?.request?.targetObject }}
        drawer
      />
      <ScreenedAndReportedResponseAssessmentCell assessment={assessment} />
      <DateCell value={inquiry?.createdAt} automationTooltip={automationTooltip} />
      <DateCell value={response?.createdAt} />
      <DateCell value={inquiry?.deadline} />
      <DateCell value={response?.submittedAt} />

      {verificationPermitted && (
        <>
          <ValueCell
            value={
              response?.verifications?.[0]?.externalVerificationComment &&
              markdownToTxt(response?.verifications?.[0]?.externalVerificationComment)
            }
          />
          <StatusCell verification value={response?.verifications?.[0]?.internalVerificationStatus} />
          <ValueCell
            value={
              response?.verifications?.[0]?.internalVerificationComment &&
              markdownToTxt(response?.verifications?.[0]?.internalVerificationComment)
            }
          />
        </>
      )}

      <ValueCell
        value={
          response?.isScraped
            ? formatMessage({ id: 'general.aiScreening' })
            : formatMessage({ id: 'general.selfReported' })
        }
      />
    </TableRow>
  )
}

export default AccessorResponseRow
